import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { RouterModule } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faFacebook,
  faYoutube,
  faLinkedin,
  faInstagram,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import { TranslateModule } from "@ngx-translate/core";
import { DomainService } from "../shared/services/domain.service";

@Component({
  selector: "app-footer",
  template: `
    <footer class="mt-auto relative">
      <!-- Concave wave shape at the top -->
      <svg
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        class="block w-full h-[60px]"
      >
        <path
          d="M0,80 L0,40 Q720,0 1440,40 L1440,80 Z"
          fill="url(#footer-gradient)"
        />
        <defs>
          <linearGradient
            id="footer-gradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stop-color="#29b9e3" />
            <stop offset="100%" stop-color="#357bbf" />
          </linearGradient>
        </defs>
      </svg>

      <div
        style="background: linear-gradient(90deg, #29b9e3 0%, #357bbf 100%);"
        class="pb-12 pt-8"
      >
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-8 text-white">
            <!-- Column 1: Meet the Youngsters -->
            <div class="text-right">
              <h3 class="font-bold mb-3 text-white text-2xl">
                {{ "Meet the Youngsters" | translate }}
              </h3>
              <ul class="list-none p-0 m-0 text-base">
                <li class="mb-2">
                  <a
                    routerLink="/pages/over-ons"
                    class="text-white no-underline hover:underline"
                    >{{ "Meet the Team" | translate }}</a
                  >
                </li>
                <li class="mb-2">
                  <a
                    routerLink="/pages/voor-bedrijven"
                    class="text-white no-underline hover:underline"
                    >{{ "MTY for employers" | translate }}</a
                  >
                </li>
                <li class="mb-2">
                  <a
                    routerLink="/pages/algemene-voorwaarden"
                    class="text-white no-underline hover:underline"
                    >{{ "Terms and conditions" | translate }}</a
                  >
                </li>
                <li class="mb-2">
                  <a
                    routerLink="/pages/privacyverklaring"
                    class="text-white no-underline hover:underline"
                    >{{ "Privacy statement" | translate }}</a
                  >
                </li>
              </ul>
            </div>

            <!-- Column 2: Social -->
            <div class="text-center">
              <h3 class="font-bold mb-3 text-white text-2xl">
                {{ "Social" | translate }}
              </h3>
              <div class="flex justify-center mb-3 gap-3">
                <a
                  target="_blank"
                  href="https://www.facebook.com/meettheyoungsters/"
                  class="no-underline w-[26px] h-[26px] bg-white rounded-full inline-flex items-center justify-center text-mty-primary"
                >
                  <fa-icon [icon]="faFacebook" class="text-sm"></fa-icon>
                </a>
                <a
                  target="_blank"
                  href="https://www.instagram.com/meettheyoungsters"
                  class="no-underline w-[26px] h-[26px] bg-white rounded-full inline-flex items-center justify-center text-mty-primary"
                >
                  <fa-icon [icon]="faInstagram" class="text-sm"></fa-icon>
                </a>
                <a
                  target="_blank"
                  href="https://www.linkedin.com/company/5316028/"
                  class="no-underline w-[26px] h-[26px] bg-white rounded-full inline-flex items-center justify-center text-mty-primary"
                >
                  <fa-icon [icon]="faLinkedin" class="text-sm"></fa-icon>
                </a>
                <a
                  target="_blank"
                  href="https://www.youtube.com/channel/UC_cWwKbrVnPIvWSVkL-S6Sg"
                  class="no-underline w-[26px] h-[26px] bg-white rounded-full inline-flex items-center justify-center text-mty-primary"
                >
                  <fa-icon [icon]="faYoutube" class="text-sm"></fa-icon>
                </a>
                <a
                  target="_blank"
                  href="https://www.tiktok.com/@meettheyoungsters?lang=nl-NL"
                  class="no-underline w-[26px] h-[26px] bg-white rounded-full inline-flex items-center justify-center text-mty-primary"
                >
                  <fa-icon [icon]="faTiktok" class="text-sm"></fa-icon>
                </a>
              </div>
              <div class="flex justify-center items-center gap-[5px]">
                <a
                  target="_blank"
                  href="https://apps.apple.com/nl/app/meet-the-youngsters/id1586327238"
                >
                  <img
                    src="assets/img/icons/apple-app-store-svg.svg"
                    alt="App Store"
                    class="w-full max-w-[120px]"
                  />
                </a>
                <a
                  class="bg-white rounded p-[5px]"
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=nl.innervate.mty"
                >
                  <img
                    src="assets/img/icons/google-play-svg.svg"
                    alt="Google Play"
                    class="w-full max-w-[120px]"
                  />
                </a>
              </div>
            </div>

            <!-- Column 3: Contact -->
            <div class="text-left">
              <h3 class="font-bold mb-3 text-white text-2xl">
                {{ "Contact" | translate }}
              </h3>
              <div class="text-base">
                @if (isBrazilDomain) {
                <p><strong>Meet the Youngsters Brasil</strong></p>
                <p>
                  Dep. Meet the Youngsters Brasil<br />Currículo Ltda<br />CNPJ
                  34.774.108/0001-53<br />
                  Rua Jaime Ovale 215 – Jardim Guanabara – Ilha do Governador<br />
                  CEP 21940 – 100 – Rio de Janeiro – RJ – Brasil<br />
                  <a
                    href="mailto:bruno@curriculo.link"
                    class="text-white no-underline hover:underline"
                    >bruno&#64;curriculo.link</a
                  ><br />
                  <a
                    href="tel:+5524992277804"
                    class="text-white no-underline hover:underline"
                    >+55 24 99227 7804</a
                  >
                </p>
                }
                <p>
                  <strong
                    >{{ "Headquarters" | translate }}<br />Meet the
                    Youngsters</strong
                  >
                </p>
                <p>
                  Avenue Ceramique 300<br />6221 KX Maastricht<br />
                  <a
                    href="mailto:info@meettheyoungsters.nl"
                    class="text-white no-underline hover:underline"
                    >info&#64;meettheyoungsters.nl</a
                  ><br />
                  <a
                    href="tel:+31683420665"
                    class="text-white no-underline hover:underline"
                    >+31 6 83 42 06 65</a
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  `,
  standalone: true,
  imports: [RouterModule, FontAwesomeModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent {
  private domain = inject(DomainService);

  readonly isBrazilDomain = this.domain.getCurrentRegion() === "BR";

  readonly faFacebook = faFacebook;
  readonly faYoutube = faYoutube;
  readonly faLinkedin = faLinkedin;
  readonly faInstagram = faInstagram;
  readonly faTiktok = faTiktok;
}
